<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import AudienceService from '@/services/AudienceService'
import { CHAT_TEXT_MAX } from '@/composables/useAudienceRoom'
import { useOptimisticActions } from '@/composables/useOptimisticActions'
import { viewerColor, viewerTag } from './viewerIdentity'
import FormButton from '@/components/Atoms/FormButton.vue'
import ActionModal from '@/components/modals/ActionModal.vue'
import AudienceComposer from './AudienceComposer.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconPin from '@/components/icon/IconPin.vue'
import IconLock from '@/components/icon/IconLock.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'

const { notify } = useNotification()

const props = defineProps({
	streamId: {
		type: String,
		required: true
	},
	messages: {
		type: Array,
		default: () => []
	},
	pinned: {
		type: Object,
		default: null
	},
	connected: {
		type: Boolean,
		default: false
	},
	/** Our own viewer id, so the host's own messages can be marked as theirs. */
	selfVid: {
		type: String,
		default: ''
	},
	canPost: {
		type: Boolean,
		default: false
	},
	/** Shown in place of the placeholder when posting is not possible. */
	blockedReason: {
		type: String,
		default: ''
	},
	sendError: {
		type: String,
		default: ''
	},
	/** Mirrors the Panel setup toggle; messages carry `country` either way. */
	showCountryFlags: {
		type: Boolean,
		default: false
	},
	/** Returns whether the frame left the socket. See useAudienceRoom.sendChat. */
	onSend: {
		type: Function,
		default: () => false
	}
})

// The ban count on the room card comes from a stats snapshot, which a ban
// placed here has just made wrong.
const emit = defineEmits(['banned'])

// Anything longer is a wall of text in a list meant for scanning. Measured in
// characters rather than by line-clamping the rendered height so the decision
// is the same on every viewport and needs no layout read.
const LONG_MESSAGE = 320

const listRef = ref(null)

// The pinned banner sits above the scroller inside a fixed-height pane, so it
// grows and shrinks at the scroller's expense: pinning used to shove the whole
// list down under the moderator's cursor. Watching the banner's box and moving
// scrollTop by exactly the height it took (or gave back) keeps whatever is on
// screen exactly where it was — and, at the bottom, keeps the bottom pinned,
// since the scrollable range grows by the same amount. ResizeObserver fires
// after layout but before paint, so no shifted frame is ever shown.
const pinnedBoxRef = ref(null)
let pinnedBoxHeight = 0
let pinnedBoxObserver = null

onMounted(() => {
	const box = pinnedBoxRef.value
	if (!box || typeof ResizeObserver === 'undefined') return

	// Fractional heights on purpose: rounding the measurement leaves the list
	// visibly off by the rounded-away remainder.
	const height = () => box.getBoundingClientRect().height

	pinnedBoxHeight = height()
	pinnedBoxObserver = new ResizeObserver(() => {
		const delta = height() - pinnedBoxHeight
		pinnedBoxHeight += delta
		if (delta && listRef.value) listRef.value.scrollTop += delta
	})
	pinnedBoxObserver.observe(box)
})

onBeforeUnmount(() => {
	if (pinnedBoxObserver) pinnedBoxObserver.disconnect()
})

const draft = ref('')
const banTarget = ref(null)
const banMinutes = ref(60)
const banModalVisible = ref(false)
const expanded = ref(new Set())

// Messages that arrived below the viewport while the moderator was scrolled
// up reading. Rendered as a jump pill instead of yanking them down.
const newSince = ref(0)

// Re-rendered on a timer only so the relative times stay honest, same as the
// Q&A pane's clock.
const now = ref(Date.now())
const clock = window.setInterval(() => { now.value = Date.now() }, 30000)
onBeforeUnmount(() => window.clearInterval(clock))

// Moderation is applied here first and reconciled against the room's own
// broadcast; see useOptimisticActions for why the overlay lives in the
// component rather than in useAudienceRoom.
const moderation = useOptimisticActions()

const banDurations = [
	{ label: '10 minutes', minutes: 10 },
	{ label: '1 hour', minutes: 60 },
	{ label: '24 hours', minutes: 1440 },
	{ label: 'Permanent', minutes: null }
]

const deleteKey = (id) => `chat.del:${id}`
const banKey = (vid) => `chat.ban:${vid}`
// Pinning is single-slot room state rather than per-row, and pinning a second
// message silently unpins the first server-side, so one key models it.
const PIN_KEY = 'chat.pin'

/** The room's messages, minus the ones we have already taken off screen. */
const visibleMessages = computed(() => props.messages.filter((m) => !moderation.has(deleteKey(m.id))))

/** The pin as the moderator has just left it, falling back to the room's. */
const effectivePinned = computed(() => moderation.get(PIN_KEY, props.pinned))

const isPinned = (message) => !!effectivePinned.value && effectivePinned.value.id === message.id

const isBanned = (message) => moderation.has(banKey(message.vid))

// The room agreeing with the overlay is what retires an entry. A delete is
// confirmed by the message leaving the list — which `chat.del` does on its own
// — so the reconciliation is a no-op rather than a second removal.
watch(() => [props.messages, props.pinned], () => {
	const present = new Set(props.messages.map((m) => m.id))
	const pinnedId = props.pinned ? props.pinned.id : null

	moderation.settle((key, effect) => {
		if (key === PIN_KEY) return pinnedId === (effect ? effect.id : null)
		if (key.startsWith('chat.del:')) return !present.has(Number(key.slice('chat.del:'.length)))
		// A ban has no broadcast to wait for and stays until the pane is left.
		return false
	})
}, { deep: true })

const nearBottom = () => {
	const list = listRef.value
	return !!list && list.scrollHeight - list.scrollTop - list.clientHeight < 80
}

// Only follow new messages when the moderator is already at the bottom, so
// reading back through history is not yanked away by a busy chat. While they
// are scrolled up, count what lands below the viewport for the jump pill.
watch(() => visibleMessages.value.length, async (len, oldLen) => {
	const list = listRef.value
	if (!list) return

	if (nearBottom()) {
		newSince.value = 0
		await nextTick()
		list.scrollTop = list.scrollHeight
		return
	}

	if (len > oldLen) newSince.value += len - oldLen
	// A delete or a chat.clear can shrink the list past the count.
	else if (len < oldLen) newSince.value = Math.min(newSince.value, len)
})

const onListScroll = () => {
	if (nearBottom()) newSince.value = 0
}

const jumpToLatest = async () => {
	newSince.value = 0
	await nextTick()
	const list = listRef.value
	if (list) list.scrollTop = list.scrollHeight
}

const post = () => {
	if (!props.canPost || !draft.value.trim()) return

	// No ack to wait for: the room echoes the message back to us like any
	// other, so the field empties now and the echo does the rendering.
	if (!props.onSend(draft.value)) return
	draft.value = ''

	// Posting is an intent to follow the conversation, so a moderator who had
	// scrolled back is brought to the bottom in time for their own echo.
	const list = listRef.value
	if (list) list.scrollTop = list.scrollHeight
}

/**
 * Coarse relative time, matching the Q&A pane: a moderator needs "just now"
 * versus "an hour ago", and the exact clock lives in the tooltip.
 */
const sentAgo = (ts) => {
	if (!ts) return ''

	const seconds = Math.max(0, Math.round((now.value - ts) / 1000))
	if (seconds < 45) return 'just now'

	const minutes = Math.round(seconds / 60)
	if (minutes < 60) return `${minutes}m ago`

	const hours = Math.floor(minutes / 60)
	if (hours < 24) return `${hours}h ${minutes % 60}m ago`

	return `${Math.floor(hours / 24)}d ago`
}

const sentAt = (ts) => (ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '')

/**
 * ISO 3166-1 alpha-2 to the regional-indicator emoji pair. Anything else —
 * null country, odd values — renders nothing rather than a broken glyph.
 */
const countryFlag = (code) => {
	if (!code || !/^[A-Za-z]{2}$/.test(code)) return ''
	return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)))
}

// A silence long enough that what follows is a different conversation. The
// separator carries the absolute time, which is exactly when relative rows
// ("5h ago" next to "5h ago") stop telling messages apart.
const GAP_MS = 30 * 60 * 1000

// Within a group, though, messages this far apart are still "the same person
// talking" — the identity line repeats only when someone else speaks or the
// author pauses long enough that the timestamp matters again.
const GROUP_MS = 5 * 60 * 1000

const gapLabel = (ts) => {
	const date = new Date(ts)
	const sameDay = date.toDateString() === new Date(now.value).toDateString()
	if (sameDay) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
	return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/**
 * The visible messages with a separator row wherever the room went quiet, and
 * a `first` flag on each message that should carry the identity line. Derived
 * from the filtered list, so deleting a group's header message re-elects the
 * next one automatically — and a rolled-back delete restores the old shape.
 */
const rows = computed(() => {
	const out = []
	let prev = null

	for (const message of visibleMessages.value) {
		const quiet = !!prev && !!message.ts && !!prev.ts && message.ts - prev.ts > GAP_MS
		if (quiet) {
			out.push({ type: 'gap', key: `gap-${message.id}`, label: gapLabel(message.ts) })
		}

		const grouped = !!prev && !quiet && prev.vid === message.vid
			&& !!message.ts && !!prev.ts && message.ts - prev.ts <= GROUP_MS

		out.push({
			type: 'message',
			key: message.id,
			message,
			first: !grouped,
			// Breathing room between groups, but not after a separator (which
			// brings its own) and not before the very first row.
			spaced: !grouped && !!prev && !quiet
		})
		prev = message
	}

	return out
})

const isLong = (message) => (message.text || '').length > LONG_MESSAGE

const bodyOf = (message) => {
	if (!isLong(message) || expanded.value.has(message.id)) return message.text
	return `${message.text.slice(0, LONG_MESSAGE).trimEnd()}…`
}

const toggleExpanded = (message) => {
	const next = new Set(expanded.value)
	if (next.has(message.id)) next.delete(message.id)
	else next.add(message.id)
	expanded.value = next
}

const removeMessage = (message) => moderation.run({
	key: deleteKey(message.id),
	request: () => AudienceService.deleteMessage(props.streamId, message.id),
	failure: 'That message is back — deleting it failed.'
})

const togglePin = (message) => {
	const pinning = !isPinned(message)

	return moderation.run({
		key: PIN_KEY,
		// Pinning replaces rather than adds, exactly as the room does it, so
		// the previously pinned message drops out of the bar on click.
		effect: pinning ? message : null,
		request: () => (pinning
			? AudienceService.pinMessage(props.streamId, message.id)
			: AudienceService.unpinMessage(props.streamId, message.id)),
		failure: pinning ? 'The message was not pinned.' : 'The message is still pinned.'
	})
}

const unpin = () => {
	const target = effectivePinned.value
	if (!target) return

	return moderation.run({
		key: PIN_KEY,
		effect: null,
		request: () => AudienceService.unpinMessage(props.streamId, target.id),
		failure: 'The message is still pinned.'
	})
}

const promptBan = (message) => {
	banTarget.value = message
	banMinutes.value = 60
	banModalVisible.value = true
}

const confirmBan = async () => {
	const target = banTarget.value
	if (!target) return

	banTarget.value = null

	const done = await moderation.run({
		key: banKey(target.vid),
		effect: true,
		request: () => AudienceService.banViewer(props.streamId, target.vid, banMinutes.value ?? undefined),
		failure: 'The viewer was not banned.'
	})

	if (!done) return

	notify({ group: 'success', text: `${target.name || 'Viewer'} was banned` })
	emit('banned')
}
</script>

<template>
	<div class="flex flex-col h-full">
		<!-- Always-rendered box around the conditional banner: the scroll
		compensation watches this element's height, and an observer cannot
		watch something v-if takes out of the DOM. Flex, not block, so the
		banner's bottom margin cannot collapse out of the measured height. -->
		<div ref="pinnedBoxRef" class="shrink-0 flex flex-col">
			<div v-if="effectivePinned"
				class="flex items-start justify-between gap-x-3 bg-surface-3 rounded-lg px-3 py-2 mb-3">
				<div class="min-w-0">
					<p class="text-tiny uppercase tracking-wide text-main mb-0.5">Pinned</p>
					<p class="text-sm text-surface-text-1 break-words mb-0" dir="auto">{{ effectivePinned.text }}</p>
				</div>
				<FormButton type="link" size="sm" @click="unpin">Unpin</FormButton>
			</div>
		</div>

		<div class="relative flex-1 min-h-0">
			<div ref="listRef" class="h-full overflow-y-auto -mx-1 px-1" @scroll.passive="onListScroll">
				<p v-if="!visibleMessages.length" class="text-sm text-surface-7 text-center py-8 mb-0">
					{{ connected ? 'No messages yet.' : 'Connect to the room to see chat.' }}
				</p>

				<template v-for="row in rows" :key="row.key">
					<!-- The room went quiet for a while; what follows is a
					different conversation, stamped with the absolute time the
					relative labels stop conveying. -->
					<div v-if="row.type === 'gap'" class="flex items-center gap-x-3 py-1.5" aria-hidden="true">
						<span class="h-px flex-1 bg-surface-3"></span>
						<span class="text-tiny text-surface-7 shrink-0">{{ row.label }}</span>
						<span class="h-px flex-1 bg-surface-3"></span>
					</div>

					<div v-else class="group flex items-start gap-x-2 rounded-md pl-2 pr-1 hover:bg-surface-2"
						:class="[
							row.first ? 'py-1' : 'py-0.5',
							row.spaced ? 'mt-2' : '',
							isPinned(row.message) ? 'bg-surface-2 shadow-[inset_2px_0_0] shadow-main' : ''
						]">
						<div class="min-w-0 flex-1">
							<!-- Who and when on one line, body on the next — but only
							once per run of messages from the same person. A busy
							chat repeating the identity line on every row buried the
							messages under their own labels. Follow-up rows carry
							the sender and exact time in their tooltip. -->
							<p v-if="row.first" class="flex items-center gap-x-1.5 leading-tight mb-0.5">
								<span class="w-1.5 h-1.5 rounded-full shrink-0"
									:style="{ backgroundColor: viewerColor(row.message.vid) }"></span>
								<span class="text-sm font-medium text-surface-text-1 truncate max-w-[12rem]">
									{{ row.message.name || 'Guest' }}
								</span>
								<span v-if="showCountryFlags && countryFlag(row.message.country)"
									class="text-sm leading-none shrink-0" :title="row.message.country">
									{{ countryFlag(row.message.country) }}
								</span>
								<span class="font-mono text-tiny text-surface-7 shrink-0"
									:title="`Viewer ${row.message.vid}`">{{ viewerTag(row.message.vid) }}</span>

								<span v-if="selfVid && row.message.vid === selfVid"
									class="text-tiny uppercase tracking-wide text-main bg-surface-3 rounded px-1 shrink-0">Host</span>
								<span v-if="isBanned(row.message)"
									class="text-tiny uppercase tracking-wide text-error bg-surface-3 rounded px-1 shrink-0">Banned</span>
								<span v-if="isPinned(row.message)"
									class="text-tiny uppercase tracking-wide text-main shrink-0">Pinned</span>

								<span class="text-tiny text-surface-7 shrink-0 ml-auto pl-1"
									:title="sentAt(row.message.ts)">
									{{ sentAgo(row.message.ts) }}
								</span>
							</p>

							<p class="text-sm text-surface-text-1 break-words leading-snug mb-0" dir="auto"
								:title="row.first ? null : `${row.message.name || 'Guest'} · ${sentAt(row.message.ts)}`">
								{{ bodyOf(row.message) }}
								<button v-if="isLong(row.message)" type="button"
									class="text-tiny text-main hover:underline whitespace-nowrap ml-1"
									@click="toggleExpanded(row.message)">
									{{ expanded.has(row.message.id) ? 'Show less' : 'Show more' }}
								</button>
							</p>
						</div>

						<!-- Revealed on row hover or keyboard focus where hover
						exists; always drawn on touch devices, which have no hover
						and would otherwise leave a moderator unable to moderate.
						Three always-on icons per row drowned the messages. -->
						<div
							class="flex items-center gap-x-0.5 shrink-0 transition-opacity [@media(hover:hover)]:opacity-0 group-hover:!opacity-100 focus-within:!opacity-100">
							<FormButton type="light" size="sm" is-icon
								:title="isPinned(row.message) ? 'Unpin message' : 'Pin message'"
								@click="togglePin(row.message)">
								<IconBase><IconPin /></IconBase>
							</FormButton>
							<FormButton type="light" size="sm" is-icon title="Delete message"
								@click="removeMessage(row.message)">
								<IconBase><IconBin /></IconBase>
							</FormButton>
							<FormButton type="light" size="sm" is-icon
								:disabled="isBanned(row.message) || (!!selfVid && row.message.vid === selfVid)"
								:title="isBanned(row.message) ? 'Viewer is banned' : 'Ban viewer'"
								@click="promptBan(row.message)">
								<IconBase><IconLock /></IconBase>
							</FormButton>
						</div>
					</div>
				</template>
			</div>

			<!-- The conversation moved on below the viewport. -->
			<button v-if="newSince" type="button"
				class="absolute bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-x-1 rounded-full bg-main hover:bg-main-light text-white text-tiny font-medium pl-3 pr-2 py-1.5 shadow-md"
				@click="jumpToLatest">
				{{ newSince }} new message{{ newSince === 1 ? '' : 's' }}
				<IconBase width="12" height="12"><IconChevronDown /></IconBase>
			</button>
		</div>

		<!-- Pinned to the foot of the pane, next to the newest messages, and
		always rendered so a dropped connection greys it out instead of
		collapsing the scroller under the moderator's cursor. -->
		<div class="shrink-0 border-t border-surface-3 mt-3 pt-3">
			<AudienceComposer v-model="draft" :maxlength="CHAT_TEXT_MAX" :can-post="canPost"
				placeholder="Say something as the host…" :blocked-reason="blockedReason"
				:send-error="sendError" submit-label="Send" :counter-at="80" @submit="post" />
		</div>

		<ActionModal title="Ban viewer" v-model:is-visible="banModalVisible" confirm-text="Ban"
			:on-submit="confirmBan">
			<p class="text-sm text-surface-text-1">
				Ban <span class="font-medium">{{ banTarget && banTarget.name || 'this viewer' }}</span>
				<span v-if="banTarget" class="font-mono text-tiny text-surface-7">
					({{ viewerTag(banTarget.vid) }})</span>
				from the room.
				Their open connections are closed immediately, and a hash of their IP is recorded so clearing
				browser storage will not get them back in.
			</p>
			<div class="flex flex-wrap gap-2 mt-3">
				<button v-for="option in banDurations" :key="option.label" type="button"
					class="text-sm px-3 py-1.5 rounded-md border"
					:class="banMinutes === option.minutes
						? 'border-main bg-main text-surface-text-2'
						: 'border-surface-4 text-surface-text-1 hover:bg-surface-2'"
					@click="banMinutes = option.minutes">
					{{ option.label }}
				</button>
			</div>
		</ActionModal>
	</div>
</template>
